import { useState } from 'react';
import { message } from 'components';
import { sendSupraSystemSubscriptionInvitation } from '../api/SupraSystemSubscriptionController';
import { isUserFriendlyError } from '../api/SupraSystemSubscriptionController/sendSupraSystemSubscriptionInvitation';

export interface SendInviteParams {
  userId: string;
  orgId: string;
}

/**
 * Custom hook for sending Supra system subscription invitations
 *
 * Manages the loading state and error handling for invitation requests.
 * Displays success/error messages to the user and provides callbacks
 * for additional success/error handling logic.
 *
 * @returns Object containing sendInvite function and loading state
 */
export const useSendSupraInvite = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Sends Supra system subscription and join brokerage invitations to a user
   *
   * @param params - Object containing userId and orgId
   * @param onSuccess - Optional callback executed on successful invitation
   * @param onError - Optional callback executed on error, receives error object
   */
  const sendInvite = async (
    { userId, orgId }: SendInviteParams,
    onSuccess?: () => void,
    onError?: (error: any) => void,
  ) => {
    try {
      setLoading(true);

      await sendSupraSystemSubscriptionInvitation({
        orgId,
        userId,
      });
      message.success('Supra subscription invitation sent');

      onSuccess?.();
    } catch (e) {
      // Determine appropriate error message for user
      // Check if the error response contains a user-friendly message
      const errorMsg = isUserFriendlyError(e?.response?.data?.message)
        ? e.response.data.message
        : 'Something went wrong please try again';

      message.error(errorMsg);

      onError?.(e);
    } finally {
      setLoading(false);
    }
  };

  // Return the invitation function and current loading state
  return { sendInvite, loading };
};
